import { useEffect, useState } from 'react';

import { fetchAllMeetingNumbers } from 'flow/actions/meeting-actions';
import { useAppDispatch, useAppSelector } from 'flow/hooks/useRedux';
import { selectAllMeetingNumbersSorted } from 'flow/selectors/meeting-selectors';
import { getOwnCountryCodeOrNull, isRetailer } from 'flow/selectors/selectors';
import { parseUrlParameter } from 'flow/utils/browser-utils';
import { IS_UAE_HOST } from 'flow/utils/browser-utils-TW';
import { prettyFormatE164Number } from 'flow/utils/call-utils';
import { FREE_PRO_INTERNATIONAL_CONFERENCE_NUMBER } from 'flow/utils/meeting-constants';
import { getConferenceLink } from 'flow/utils/meeting-utils';
import { Retailers } from 'flow/utils/retailer-utils';

/**
 * The country-selectable dial-in number + matching video link, shared by every
 * joining-info surface (side panel, lobby modal). Defaults to the viewer's own
 * country, falling back to the meeting's own default number until the sorted
 * list loads.
 */
const useMeetingCountryNumber = (
    meetingCode: string,
    fallbackNumber: string,
) => {
    const dispatch = useAppDispatch();
    const ownCountryCode = useAppSelector(getOwnCountryCodeOrNull);
    const meetingNumbers = useAppSelector(selectAllMeetingNumbersSorted);
    const isRetailerFreePro = useAppSelector((state) =>
        isRetailer(state, Retailers.FREEPRO),
    );

    const countryOptions = Array.from(meetingNumbers.values()).map(
        (number) => ({
            key: number.countryCode,
            label: number.countryNameInEnglish,
        }),
    );

    const getInitialCountryCode = () => {
        if (IS_UAE_HOST) {
            return 'ae';
        }
        if (ownCountryCode) {
            return ownCountryCode.toLowerCase();
        }
        if (parseUrlParameter('countryCode')) {
            return parseUrlParameter('countryCode');
        }
        if (meetingNumbers && countryOptions.length > 0) {
            return countryOptions[0].key;
        }
        return null;
    };

    const [countryCode, setCountryCode] = useState(getInitialCountryCode());
    const [countryNumber, setCountryNumber] = useState(fallbackNumber);

    useEffect(() => {
        dispatch(fetchAllMeetingNumbers());
    }, [dispatch]);

    useEffect(() => {
        if (countryCode === 'fr' && isRetailerFreePro) {
            setCountryNumber(FREE_PRO_INTERNATIONAL_CONFERENCE_NUMBER);
            return;
        }
        const country = meetingNumbers?.get(countryCode);
        if (country) {
            setCountryNumber(
                prettyFormatE164Number(
                    country.conferenceNumber,
                    'wantInternational',
                ),
            );
        }
    }, [meetingNumbers, countryCode, isRetailerFreePro]);

    const conferenceLink = getConferenceLink(meetingCode, countryCode);

    return {
        countryCode,
        setCountryCode,
        countryOptions,
        countryNumber,
        conferenceLink,
    };
};

export default useMeetingCountryNumber;
